import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import GameCanvas from './GameCanvas';
import Instructions from './Instructions';
import { useGameState } from '../hooks/useGameState';
import { useModules } from '../hooks/useModules';
import { useAvatar } from '../hooks/useAvatar';

const LearningWorld = ({ modules = [], currentUser, csrfToken }) => {
  const containerRef = useRef(null);
  const [isMobile, setIsMobile] = useState(false);

  // Make currentUser available globally for API calls
  useEffect(() => {
    window.currentUser = currentUser;
  }, [currentUser]);

  const {
    canvasSize,
    setCanvasSize,
    position,
    setPosition,
    isMoving,
    wobbleOffset,
    keys,
    setKeys,
    handleKeyDown,
    handleKeyUp,
    gems,
    collectedGems,
    enemies,
    enemySpeedMultiplier,
    isInvulnerable
  } = useGameState();

  // Check if device is mobile but don't redirect
  useEffect(() => {
    const checkMobile = () => {
      const isMobileDevice = window.innerWidth < 768 ||
                            /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      setIsMobile(isMobileDevice);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Make collected gems available to other components
  useEffect(() => {
    window.collectedGems = collectedGems;
  }, [collectedGems]);

  const {
    localModules,
    activeModule,
    getModulePositions,
    handleModuleClick
  } = useModules(modules, position, canvasSize, currentUser);

  const { avatarImage } = useAvatar(currentUser);

  // Update canvas size on mount and window resize
  useEffect(() => {
    const updateCanvasSize = () => {
      if (containerRef.current) {
        // Use a fixed width for consistency between public and authenticated versions
        const width = 1200; // Fixed width for consistency
        const height = 700; // Fixed height for consistency
        setCanvasSize({ width, height });
        setPosition(prev => ({ ...prev, x: width / 2, y: height / 2 }));
      }
    };

    setTimeout(updateCanvasSize, 100);
    window.addEventListener('resize', updateCanvasSize);
    return () => window.removeEventListener('resize', updateCanvasSize);
  }, []);

  // Set up keyboard listeners
  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);

    // Add Enter key handler for module activation
    const handleEnterKey = (e) => {
      if (e.key === 'Enter' && activeModule !== null) {
        handleModuleClick();
      }
    };

    window.addEventListener('keydown', handleEnterKey);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
      window.removeEventListener('keydown', handleEnterKey);
    };
  }, [handleKeyDown, handleKeyUp, activeModule, handleModuleClick]);

  // If on mobile, show mobile-optimized version
  if (isMobile) {
    return (
      <div className="relative w-full h-[500px] bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl overflow-hidden shadow-2xl opacity-90">
        <GameCanvas
          canvasSize={{ width: window.innerWidth - 32, height: 500 }}
          position={position}
          isMoving={isMoving}
          wobbleOffset={wobbleOffset}
          avatarImage={avatarImage}
          modules={localModules}
          activeModule={activeModule}
          getModulePositions={getModulePositions}
          gems={gems}
          collectedGems={collectedGems}
          enemies={enemies}
          enemySpeedMultiplier={enemySpeedMultiplier}
          isInvulnerable={isInvulnerable}
        />

        {/* Mobile Controls */}
        <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex gap-4">
          <div className="grid grid-cols-3 gap-2 bg-white bg-opacity-50 p-2 rounded-lg">
            <button
              className="w-12 h-12 bg-white bg-opacity-75 rounded-lg flex items-center justify-center"
              onTouchStart={() => setKeys(prev => ({ ...prev, ArrowLeft: true }))}
              onTouchEnd={() => setKeys(prev => ({ ...prev, ArrowLeft: false }))}
            >
              ←
            </button>
            <button
              className="w-12 h-12 bg-white bg-opacity-75 rounded-lg flex items-center justify-center"
              onTouchStart={() => setKeys(prev => ({ ...prev, ArrowUp: true }))}
              onTouchEnd={() => setKeys(prev => ({ ...prev, ArrowUp: false }))}
            >
              ↑
            </button>
            <button
              className="w-12 h-12 bg-white bg-opacity-75 rounded-lg flex items-center justify-center"
              onTouchStart={() => setKeys(prev => ({ ...prev, ArrowRight: true }))}
              onTouchEnd={() => setKeys(prev => ({ ...prev, ArrowRight: false }))}
            >
              →
            </button>
            <div></div>
            <button
              className="w-12 h-12 bg-white bg-opacity-75 rounded-lg flex items-center justify-center"
              onTouchStart={() => setKeys(prev => ({ ...prev, ArrowDown: true }))}
              onTouchEnd={() => setKeys(prev => ({ ...prev, ArrowDown: false }))}
            >
              ↓
            </button>
            <div></div>
          </div>
          <button
            className="w-12 h-12 bg-white bg-opacity-75 rounded-lg flex items-center justify-center"
            onClick={handleModuleClick}
          >
            ✓
          </button>
        </div>

        <Instructions currentUser={currentUser} />

        {/* Navigation button */}
        <div className="absolute top-4 right-4">
          <a href="?view=legacy" className="bg-white bg-opacity-90 px-4 py-2 rounded-lg shadow-md text-sm font-medium hover:bg-opacity-100 transition-all">
            Switch to Classic View
          </a>
        </div>
      </div>
    );
  }

  if (!localModules || !Array.isArray(localModules) || localModules.length === 0) {
    return (
      <div className="p-8 bg-gradient-to-br from-blue-50 to-indigo-100 rounded-xl">
        <h2 className="text-2xl font-bold mb-4">Learning World</h2>
        <p>No learning modules available</p>
        <div className="mt-4">
          <a href="?view=legacy" className="px-4 py-2 bg-gray-200 rounded">
            Switch to Classic View
          </a>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className="relative w-full h-[700px] bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl overflow-hidden shadow-2xl opacity-90"
      style={{ width: "1200px", height: "700px", maxWidth: "100%", margin: "0 auto" }}
    >
      <GameCanvas
        canvasSize={canvasSize}
        position={position}
        isMoving={isMoving}
        wobbleOffset={wobbleOffset}
        avatarImage={avatarImage}
        modules={localModules}
        activeModule={activeModule}
        getModulePositions={getModulePositions}
        gems={gems}
        collectedGems={collectedGems}
        enemies={enemies}
        enemySpeedMultiplier={enemySpeedMultiplier}
        isInvulnerable={isInvulnerable}
      />

      <Instructions currentUser={currentUser} />

      {/* Navigation button */}
      <div className="absolute top-4 right-4">
        {currentUser ? (
          <a href="?view=legacy" className="bg-white bg-opacity-90 px-4 py-2 rounded-lg shadow-md text-sm font-medium hover:bg-opacity-100 transition-all">
            Switch to Classic View
          </a>
        ) : (
          <a href="/" className="bg-white bg-opacity-90 px-4 py-2 rounded-lg shadow-md text-sm font-medium hover:bg-opacity-100 transition-all">
            Back to Home
          </a>
        )}
      </div>
    </div>
  );
};

LearningWorld.propTypes = {
  modules: PropTypes.array,
  currentUser: PropTypes.object,
  csrfToken: PropTypes.string
};

export default LearningWorld;